/**
 * The message step of a push campaign.
 *
 * A push is a title, a short line of body, and an optional picture and link -
 * nothing like an email, and nothing like a WhatsApp template. The preview
 * shows it as a phone would, a notification on a lock screen, because that is
 * the whole of what a recipient sees.
 *
 * There are three ways to target it. "App users" is the real one: it sends to
 * everyone who installed and logged into a XYRA app, read live from the app's
 * device directory, and can narrow to one app. "Topic" fans out to a Firebase
 * topic. "Specific devices" sends to hand-typed tokens - the safe way to test
 * on one phone without touching live users.
 */
import { useEffect, useState } from 'react';
import { applyMerge } from '../../domain/marketing.js';
import { Field } from '../Sheet.jsx';
import api from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';
import Icon from '../../lib/icons.jsx';

export default function PushStep({ form, set, sample, defaultTopic }) {
  const title = String(form.pushTitle || '');
  const body = String(form.pushBody || '');
  const topic = form.pushTopic || defaultTopic || '';
  const mode = form.pushMode || 'app';
  const tokenCount = String(form.pushTokens || '').split('\n').filter(x => x.trim()).length;

  const shownTitle = sample ? applyMerge(title, sample) : title;
  const shownBody = sample ? applyMerge(body, sample) : body;

  // The live count of reachable app users, per app, so a campaign can see how
  // many it will reach before it sends.
  const [aud, setAud] = useState(null);
  useEffect(() => {
    if (isOffline()) return;
    let live = true;
    api.push.audience().then(a => { if (live) setAud(a); }).catch(() => { if (live) setAud({ connected: false, total: 0, byApp: {} }); });
    return () => { live = false; };
  }, []);

  const byApp = (aud && aud.byApp) || {};
  const apps = Object.keys(byApp).sort();
  const appTotal = aud ? aud.total : null;
  const chosenApp = form.pushApp || 'all';
  const appReach = chosenApp === 'all' ? appTotal : (byApp[chosenApp] || 0);

  return (
    <>
      <Field
        label="Title"
        wide
        hint={<div className="fhint">{title.length}/50 — a push title is short; phones cut a long one.</div>}
      >
        <input
          autoFocus
          value={title}
          maxLength={80}
          placeholder="Your invoice is ready"
          onChange={e => set('pushTitle', e.target.value)}
        />
      </Field>

      <Field
        label="Message"
        wide
        hint={<div className="fhint">{body.length}/120 — the line under the title.</div>}
      >
        <textarea
          value={body}
          maxLength={240}
          style={{ minHeight: 70 }}
          placeholder="Tap to view and download it in the app."
          onChange={e => set('pushBody', e.target.value)}
        />
      </Field>

      <Field
        label="Link when tapped"
        wide
        hint={<div className="fhint">Opened when the notification is tapped — a web address or an app deep link. Optional.</div>}
      >
        <input
          value={form.pushLink || ''}
          placeholder="https://thexyra.ai/invoices"
          onChange={e => set('pushLink', e.target.value.trim())}
        />
      </Field>

      <Field
        label="Image"
        wide
        hint={<div className="fhint">Shown in the expanded notification. A public URL. Optional.</div>}
      >
        <input
          value={form.pushImage || ''}
          placeholder="https://…/banner.png"
          onChange={e => set('pushImage', e.target.value.trim())}
        />
      </Field>

      <Field label="Who gets it" wide>
        <div className="chips">
          <button type="button" className={`chip${mode === 'app' ? ' on' : ''}`} onClick={() => set('pushMode', 'app')}>
            <Icon name="users" />App users
          </button>
          <button type="button" className={`chip${mode === 'topic' ? ' on' : ''}`} onClick={() => set('pushMode', 'topic')}>
            <Icon name="bell" />A topic
          </button>
          <button type="button" className={`chip${mode === 'test' ? ' on' : ''}`} onClick={() => set('pushMode', 'test')}>
            <Icon name="phone" />Specific devices (test)
          </button>
        </div>
      </Field>

      {mode === 'app' ? (
        <Field
          label="Which app"
          wide
          hint={
            <div className="fhint">
              {aud && !aud.connected
                ? 'The app user list is not connected — add the device API token in Channel setup.'
                : 'Sends to everyone who installed and logged into the app. Read live when it goes out.'}
            </div>
          }
        >
          <select value={chosenApp} onChange={e => set('pushApp', e.target.value)}>
            <option value="all">All apps{appTotal != null ? ` — ${appTotal} users` : ''}</option>
            {apps.map(a => (
              <option key={a} value={a}>{a} — {byApp[a]} users</option>
            ))}
          </select>
        </Field>
      ) : mode === 'test' ? (
        <Field
          label="Device tokens"
          wide
          hint={
            <div className="fhint">
              One FCM token per line — only these phones get it, nobody else. Ask your app
              developer for a test device&rsquo;s token, or print it from the app on the phone
              you are testing with. This is how you test without touching live users.
            </div>
          }
        >
          <textarea
            style={{ minHeight: 80, fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 12 }}
            value={form.pushTokens || ''}
            placeholder="fGc3…long-token…xYz&#10;another-device-token…"
            onChange={e => set('pushTokens', e.target.value)}
          />
        </Field>
      ) : (
        <Field
          label="Send to topic"
          wide
          hint={
            <div className="fhint" style={{ color: 'var(--ch-sms)' }}>
              Reaches <b>every</b> device subscribed to this topic — all live users included.
              {defaultTopic ? ` Blank uses the default, ${defaultTopic}.` : ''}
            </div>
          }
        >
          <input
            value={form.pushTopic || ''}
            placeholder={defaultTopic || 'xyra-all'}
            onChange={e => set('pushTopic', e.target.value.trim())}
          />
        </Field>
      )}

      <Field label="Preview" wide>
        <div className="pushprev">
          <div className="pushnotif">
            <div className="pushicon"><Icon name="bell" /></div>
            <div className="pushbodywrap">
              <div className="pushhead">
                <b>XYRA</b>
                <span>now</span>
              </div>
              <div className="pushtitle">{shownTitle || 'Your title'}</div>
              <div className="pushtext">{shownBody || 'Your message shows here.'}</div>
            </div>
            {form.pushImage ? <img className="pushimg" src={form.pushImage} alt="" /> : null}
          </div>
          <div className="fhint" style={{ marginTop: 8 }}>
            {mode === 'app'
              ? <>Goes to <b>{appReach != null ? `${appReach} app user(s)` : '…'}</b>{chosenApp !== 'all' ? <> on <b>{chosenApp}</b></> : null} — real users.</>
              : mode === 'test'
                ? <>Goes only to the <b>{tokenCount} device(s)</b> you listed — safe for testing.</>
                : <>Goes to <b>{topic || 'no topic yet'}</b> — every subscribed device.</>}
            {' '}Push confirms it was handed to the device, not that anybody saw it — no read receipt.
          </div>
        </div>
      </Field>
    </>
  );
}
